import RecordVoiceOverRoundedIcon from "@mui/icons-material/RecordVoiceOverRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import React, { useState } from "react";
import { CircleFlag } from "../../common/CircleFlag";
import { useMatchCommentary } from "../../common/contexts/aiCommentaryContext";
import CommentaryModal from "../../Mobile/components/Commentary/CommentaryModal";
import { Bet } from "../../types/Bet";
import { Match } from "../../types/Match";
import countryDict from "../../utils/countryDict";
import { getDateFromDate, getLocalTimeFromDate } from "../../utils/timeUtils";
import {
  getWinner,
  isTbd,
  isTbdMatch,
  wasDecidedInExtraTime,
  wasDecidedOnPenalties,
} from "./bracketLayout";

interface BracketMatchCardProps {
  match: Match;
  bet?: Bet;
  /** Bigger styling for the centre Final node. */
  variant?: "default" | "final" | "third";
  onSelect: (match: Match) => void;
}

const flagCode = (name?: string) => (name && countryDict.get(name)) || "pl";

interface RowProps {
  team: Match["homeTeam"];
  score: number | null;
  pen: number | null | undefined;
  isWinner: boolean;
  isLoser: boolean;
}

const TeamRow: React.FC<RowProps> = ({ team, score, pen, isWinner, isLoser }) => {
  const tbd = isTbd(team);
  const name = team.internationalName;
  const classes = [
    "bracketCard__row",
    isWinner ? "bracketCard__row--winner" : "",
    isLoser ? "bracketCard__row--loser" : "",
    tbd ? "bracketCard__row--tbd" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes}>
      <span className="bracketCard__flag">
        {tbd ? (
          <span className="bracketCard__flagTbd" aria-hidden />
        ) : (
          <CircleFlag countryCode={flagCode(name)} height={22} width={22} />
        )}
      </span>
      <span className="bracketCard__name">{tbd ? "TBD" : name}</span>
      {score !== null && score !== undefined && (
        <span className="bracketCard__score">
          {score}
          {pen !== null && pen !== undefined && (
            <span className="bracketCard__pen"> ({pen})</span>
          )}
        </span>
      )}
    </div>
  );
};

const BracketMatchCard: React.FC<BracketMatchCardProps> = ({
  match,
  bet,
  variant = "default",
  onSelect,
}) => {
  const winner = getWinner(match);
  const played = match.wasPlayed;
  const tbd = isTbdMatch(match);
  const pens = wasDecidedOnPenalties(match);
  const aet = wasDecidedInExtraTime(match);
  const clickable = !tbd;

  const date = match.kickOffTime?.dateTime || "";
  const dateLabel = date ? getDateFromDate(date) : "";
  const timeLabel = date ? getLocalTimeFromDate(date) : "";

  const { hasCommentary } = useMatchCommentary(match.id);
  const showCommentary = !tbd && hasCommentary;
  const [commentaryOpen, setCommentaryOpen] = useState(false);

  const openCommentary = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCommentaryOpen(true);
  };

  const handleSelect = () => {
    if (clickable) onSelect(match);
  };

  const classes = [
    "bracketCard",
    `bracketCard--${variant}`,
    clickable ? "bracketCard--clickable" : "bracketCard--locked",
    tbd ? "bracketCard--tbd" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={classes}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? handleSelect : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleSelect();
              }
            }
          : undefined
      }
    >
      <TeamRow
        team={match.homeTeam}
        score={played ? match.homeScoreRegular : null}
        pen={pens ? match.homeScorePenalty : null}
        isWinner={winner === "home"}
        isLoser={played && winner === "away"}
      />
      <TeamRow
        team={match.awayTeam}
        score={played ? match.awayScoreRegular : null}
        pen={pens ? match.awayScorePenalty : null}
        isWinner={winner === "away"}
        isLoser={played && winner === "home"}
      />

      <div className="bracketCard__footer">
        <div className="bracketCard__metaRow">
          {tbd ? (
            <span className="bracketCard__when bracketCard__when--tbd">
              Awaiting teams
            </span>
          ) : (
            <span className="bracketCard__when">
              <ScheduleRoundedIcon className="bracketCard__whenIcon" />
              {dateLabel}
              {timeLabel && <> · {timeLabel}</>}
            </span>
          )}
          {played && (
            <span className="bracketCard__ft">{pens ? "Pens" : aet ? "AET" : "FT"}</span>
          )}
        </div>

        {(showCommentary || bet || (!played && !tbd)) && (
          <div className="bracketCard__actionRow">
            {showCommentary && (
              <button
                type="button"
                className="bracketCard__commentary"
                onClick={openCommentary}
                title="AI pundit commentary"
              >
                <RecordVoiceOverRoundedIcon className="bracketCard__commentaryIcon" />
                AI Studio
              </button>
            )}
            {bet ? (
              <span className="bracketCard__bet" title="Your prediction">
                {bet.homeTeamScoreBet ?? '?'}–{bet.awayTeamScoreBet ?? '?'}
              </span>
            ) : (
              !played &&
              !tbd && (
                <span className="bracketCard__bet bracketCard__bet--empty">
                  Predict
                </span>
              )
            )}
          </div>
        )}
      </div>

      {showCommentary && (
        <CommentaryModal
          open={commentaryOpen}
          onClose={() => setCommentaryOpen(false)}
          matchId={match.id}
          homeName={match.homeTeam.internationalName}
          awayName={match.awayTeam.internationalName}
        />
      )}
    </div>
  );
};

export default BracketMatchCard;
